// frontend/src/components/ContactSection.jsx
import React, { useState } from "react";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    course: "",
    message: "",
  });

  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTermsChange = (e) => {
    setAcceptedTerms(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    // Validation
    if (!formData.name || !formData.phone || !formData.course || !formData.message) {
      setSubmitStatus({
        type: "error",
        message: "Please fill in all required fields.",
      });
      setIsSubmitting(false);
      return;
    }

    if (!acceptedTerms) {
      setSubmitStatus({
        type: "error",
        message: "You must accept the Terms & Policies to submit the form.",
      });
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "bd770e2c-eb3b-4e1d-9ddb-f2047094aa9c",
          name: formData.name,
          phone: formData.phone,
          email: formData.email || "Not provided",
          course: formData.course,
          message: formData.message,
          subject: "New Contact / Booking Request - drivewithportland.netlify.app",
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Build WhatsApp message
        const whatsappMessage = `
New contact / booking request from website

Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email || "Not provided"}
Course: ${formData.course}
Message:
${formData.message}

Sent from: https://drivewithportland.com
        `.trim();

        const encoded = encodeURIComponent(whatsappMessage);

        // Open WhatsApp in new tab
        window.open(`https://wa.me/447726778000?text=${encoded}`, "_blank");

        setSubmitStatus({
          type: "success",
          message: "Request sent successfully! WhatsApp should open now...",
        });

        // Clear form
        setFormData({
          name: "",
          email: "",
          phone: "",
          course: "",
          message: "",
        });
        setAcceptedTerms(false);
      } else {
        setSubmitStatus({
          type: "error",
          message: result.message || "Failed to send request. Please try again.",
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative 
    pt-2 sm:pt-3 lg:pt-4           /* minimal – box feels glued to navbar */
    pb-12 sm:pb-16 lg:pb-20        /* still comfortable at bottom */
    bg-[#F3F4F6] 
    px-4 sm:px-6 lg:px-12
  "
    >
      <div className="absolute -top-32 left-4 sm:left-10 w-56 sm:w-72 h-56 sm:h-72 rounded-full bg-gradient-to-tr from-purple-400/30 to-pink-300/20 filter blur-3xl animate-float-slow"></div>
      <div className="absolute -bottom-32 right-4 sm:right-10 w-72 sm:w-96 h-72 sm:h-96 rounded-full bg-gradient-to-tr from-blue-400/20 to-indigo-400/10 filter blur-3xl animate-float-slow"></div>

      <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-start bg-white backdrop-blur-xl rounded-3xl shadow-2xl p-6 sm:p-10 lg:p-16">
        <div className="col-span-full text-center mb-8 sm:mb-10 lg:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1D4ED8]">
            Get in Touch
          </h1>
        </div>

        {/* Left side - Contact Info */}
        <div className="space-y-6 text-gray-800">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1F2937] text-lg md:text-xl">
            Our Office
          </h2>

          <div className="flex items-start sm:items-center pt-6 sm:pt-10 space-x-4 group cursor-pointer">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-gray-200 group-hover:bg-gradient-to-r group-hover:from-blue-900 group-hover:to-blue-600 transition-all duration-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 group-hover:text-white transition-colors duration-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 11c1.104 0 2-.896 2-2s-.896-2-2-2-2 .896-2 2 .896 2 2 2z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 2C8.686 2 6 4.686 6 8c0 4.418 6 12 6 12s6-7.582 6-12c0-3.314-2.686-6-6-6z"
                />
              </svg>
            </div>
            <div>
              <h4 className="font-bold text-lg sm:text-xl">Address:</h4>
              <p className="text-sm sm:text-lg text-gray-600">
              Glasgow & Neighbourhoods, United Kingdom
              </p>
            </div>
          </div>

          <div className="flex items-start sm:items-center pt-6 space-x-4 group cursor-pointer">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-gray-200 group-hover:bg-gradient-to-r group-hover:from-blue-900 group-hover:to-blue-600 transition-all duration-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 group-hover:text-white transition-colors duration-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.6a2 2 0 011.7.9l1.2 1.8a2 2 0 01-.45 2.7l-1.5 1.2a12.042 12.042 0 005.5 5.5l1.2-1.5a2 2 0 012.7-.45l1.8 1.2a2 2 0 01.9 1.7V19a2 2 0 01-2 2h-1C8.477 21 3 15.523 3 9V5z"
                />
              </svg>
            </div>
            <div>
              <h4 className="font-bold text-lg sm:text-xl">Phone:</h4>
              <p className="text-sm sm:text-lg text-gray-600">
                +44 7726 77 8000
              </p>
            </div>
          </div>

          <div className="flex items-start sm:items-center pt-6 space-x-4 group cursor-pointer">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-gray-200 group-hover:bg-gradient-to-r group-hover:from-blue-900 group-hover:to-blue-600 transition-all duration-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 group-hover:text-white transition-colors duration-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h4 className="font-bold text-lg sm:text-xl">Opening Hours:</h4>
              <p className="text-sm sm:text-lg">
                <strong>Mon - Fri:</strong> 8:00 am – 8:00 pm
                <br />
                <strong>Sat - Sun:</strong> 10:00 am – 5:00 pm
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:gap-6">
          <div className="relative">
            <input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Full Name"
              className="peer w-full p-3 sm:p-4 pt-6 rounded-xl border border-gray-300 bg-transparent text-gray-900 placeholder-transparent focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all"
            />
            <label
              htmlFor="name"
              className="absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-500 text-sm peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-yellow-400 peer-focus:text-sm transition-all"
            >
              Full Name *
            </label>
          </div>

          <div className="relative">
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address"
              className="peer w-full p-3 sm:p-4 pt-6 rounded-xl border border-gray-300 bg-transparent text-gray-900 placeholder-transparent focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all"
            />
            <label
              htmlFor="email"
              className="absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-500 text-sm peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-yellow-400 peer-focus:text-sm transition-all"
            >
              Email Address
            </label>
          </div>

          <div className="relative">
            <input
              type="tel"
              name="phone"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="Phone Number"
              className="peer w-full p-3 sm:p-4 pt-6 rounded-xl border border-gray-300 bg-transparent text-gray-900 placeholder-transparent focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all"
            />
            <label
              htmlFor="phone"
              className="absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-500 text-sm peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-yellow-400 peer-focus:text-sm transition-all"
            >
              Phone Number *
            </label>
          </div>

          <div className="relative">
            <select
              name="course"
              id="course"
              value={formData.course}
              onChange={handleChange}
              required
              className="peer w-full p-3 sm:p-4 pt-6 rounded-xl border border-gray-300 bg-transparent text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all"
            >
              <option value="" disabled>
                Select Course *
              </option>
              <option value="Automatic Driving">Automatic Driving</option>
              <option value="Manual Driving">Manual Driving</option>
              <option value="Intensive Course">Intensive Course</option>
              <option value="Pass Plus">Pass Plus</option>
              <option value="Motorway Training">Motorway Training</option>
              <option value="Refresher Lessons">Refresher Lessons</option>
              <option value="Block Booking">Block Booking</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="relative">
            <textarea
              name="message"
              id="message"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Your Message"
              className="peer w-full p-3 sm:p-4 pt-6 rounded-xl border border-gray-300 bg-transparent text-gray-900 placeholder-transparent focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all"
            ></textarea>
            <label
              htmlFor="message"
              className="absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-500 text-sm peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-yellow-400 peer-focus:text-sm transition-all"
            >
              Your Message *
            </label>
          </div>

          {/* New required checkbox */}
          <div className="flex items-start gap-3 mt-2">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={handleTermsChange}
              className="mt-1.5 h-5 w-5 rounded border-gray-300 text-[#1D4ED8] focus:ring-[#1D4ED8]"
              required
            />
            <label
              htmlFor="terms"
              className="text-sm text-gray-700 leading-relaxed cursor-pointer select-none"
            >
              I have read and accept the{" "}
              <a
                href="/#faq"
                className="text-[#1D4ED8] hover:underline font-medium"
                target="_self"
              >
                Terms & Policies
              </a>
            </label>
          </div>

          {submitStatus && (
            <div
              className={`p-3 rounded-lg ${
                submitStatus.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {submitStatus.message}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`py-2 sm:py-3 px-4 sm:px-6 rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold shadow-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                isSubmitting ? "opacity-70" : ""
              }`}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;